import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";
import { takePendingPlan, startCheckout, PAID_CHECKOUT_ENABLED } from "../lib/billing";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Dev unlock without Supabase (local development only)
const isDevUnlocked = () => {
  if (process.env.REACT_APP_AI_ENABLED === "true") return true;
  try {
    return window.localStorage.getItem("frontplay_ai_dev") === "1";
  } catch {
    return false;
  }
};

const normalizeTier = (plan) => {
  if (plan === "business") return "business";
  if (plan === "paid" || plan === "pro") return "pro";
  if (plan === "light") return "light";
  return "free";
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [plan, setPlan] = useState(null); // 'free' | 'light' | 'pro' | 'business' | 'paid' | null
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(isSupabaseConfigured);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [usageModalOpen, setUsageModalOpen] = useState(false);
  const [billingNotice, setBillingNotice] = useState("");

  const fetchPlan = useCallback(async (userId) => {
    if (!supabase || !userId) {
      setPlan(null);
      setIsAdmin(false);
      return null;
    }
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("plan,is_admin")
        .eq("id", userId)
        .single();
      if (error || !data) {
        setPlan("free");
        setIsAdmin(false);
        return "free";
      }
      const next = data.plan || "free";
      setPlan(next);
      setIsAdmin(Boolean(data.is_admin));
      return next;
    } catch {
      setPlan("free");
      setIsAdmin(false);
      return "free";
    }
  }, []);

  const refreshPlan = useCallback(async () => {
    const userId = session?.user?.id;
    if (!userId) return null;
    return fetchPlan(userId);
  }, [fetchPlan, session?.user?.id]);

  useEffect(() => {
    if (!supabase) return;
    let mounted = true;

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!mounted) return;
      setSession(s);
      if (s?.user) {
        fetchPlan(s.user.id).finally(() => mounted && setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (s?.user) {
        fetchPlan(s.user.id);
      } else {
        setPlan(null);
        setIsAdmin(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchPlan]);

  // After magic-link login: resume pending plan checkout if user chose Light/Pro first.
  useEffect(() => {
    if (!PAID_CHECKOUT_ENABLED) return;
    if (!session?.access_token) return;
    const pending = takePendingPlan();
    if (!pending) return;
    startCheckout({
      plan: pending,
      accessToken: session.access_token,
      returnUrl: window.location.origin,
    }).catch(() => {
      setPlanModalOpen(true);
    });
  }, [session?.access_token]);

  // Return from Stripe Checkout (?billing=success|cancel)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const billing = params.get("billing");
    if (!billing) return;
    if (billing === "success") {
      setBillingNotice("success");
      refreshPlan();
      // Poll a few times — webhook may lag a second behind redirect
      let n = 0;
      const timer = setInterval(() => {
        n += 1;
        refreshPlan();
        if (n >= 6) clearInterval(timer);
      }, 1500);
      params.delete("billing");
      params.delete("plan");
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ""}${window.location.hash || ""}`;
      window.history.replaceState({}, "", next);
      return () => clearInterval(timer);
    }
    if (billing === "cancel") {
      setBillingNotice("cancel");
      params.delete("billing");
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ""}${window.location.hash || ""}`;
      window.history.replaceState({}, "", next);
    }
  }, [refreshPlan]);

  const signInWithEmail = useCallback(async (email) => {
    if (!supabase) throw new Error("Supabase is not configured");
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
  }, []);

  const openLoginModal = useCallback(() => setLoginModalOpen(true), []);
  const closeLoginModal = useCallback(() => setLoginModalOpen(false), []);
  const openPlanModal = useCallback(() => setPlanModalOpen(true), []);
  const closePlanModal = useCallback(() => setPlanModalOpen(false), []);
  const openUsageModal = useCallback(() => setUsageModalOpen(true), []);
  const closeUsageModal = useCallback(() => setUsageModalOpen(false), []);
  const clearBillingNotice = useCallback(() => setBillingNotice(""), []);

  const user = session?.user || null;
  const devUnlocked = !isSupabaseConfigured && isDevUnlocked();
  const tier = plan ? normalizeTier(plan) : "free";

  const value = {
    session,
    user,
    plan,
    tier,
    isAdmin,
    loading,
    isLoggedIn: Boolean(user),
    isPaid: tier === "light" || tier === "pro" || tier === "business",
    // AI edit is allowed for any logged-in user (free gets a trial quota),
    // or in dev-unlock mode without Supabase. The server enforces the quota.
    canUseAi: devUnlocked || Boolean(user),
    devUnlocked,
    signInWithEmail,
    signOut,
    loginModalOpen,
    openLoginModal,
    closeLoginModal,
    planModalOpen,
    openPlanModal,
    closePlanModal,
    usageModalOpen,
    openUsageModal,
    closeUsageModal,
    refreshPlan,
    billingNotice,
    clearBillingNotice,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
